import { getSampleRole, getSortedOvertimeMultipliers, getStraightTimeMultiplier, getMultiplierListString, getMultiplierListStringHorizontal, getPrevailingRateMultiplier, generatePayCalculationRows, generatePremiumPayCalculationRows, getThreshold } from '@/utils/rateCalculations';
import type { MealBreakConfig, Role, RateMultiplier } from '@/types';
import type { BreakBillingPolicy } from '@/types/MealBreakConfig';

/** For example calculations, derive whether a break of the given length is unpaid under the current policy. */
export function isBreakUnpaid(policy: BreakBillingPolicy | null | undefined, breakMinutes: number): boolean {
  if (!policy) return true; // default: unpaid
  if (policy.thresholdMinutes == null) return policy.upToThreshold === 'unpaid';
  return breakMinutes > policy.thresholdMinutes
    ? policy.overThreshold === 'unpaid'
    : policy.upToThreshold === 'unpaid';
}
import { formatTimeFromMinutes, formatTimeRange, formatMinutesHuman, getOrdinal, formatCurrency, formatHours } from './markdownSharedUtils';
import { calculateMultiplierOffsetExamples } from './multiplierOffsetExample';

export function calculateMealBreakMarkdownVariables(
  safeConfig: MealBreakConfig,
  feeScheduleString: string,
  roles: Role[] = [],
  rateMultipliers: RateMultiplier[] = [],
  minimumBillableHours: number = 10
) {
  const sampleRole = getSampleRole(roles);
  
  const single_offset_multiplier_list_str = getMultiplierListString(rateMultipliers, sampleRole.billingStructure, true, 1);
  const double_offset_multiplier_list_str = getMultiplierListString(rateMultipliers, sampleRole.billingStructure, true, 2);
  
  // Get the rate multiplier examples for escalation description
  const escalationStraightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const hourlyOvertimeMultipliers = getSortedOvertimeMultipliers(rateMultipliers, 'hourly');
  const dayRateOvertimeMultipliers = getSortedOvertimeMultipliers(rateMultipliers, 'day_rate');
  
  const escalationOvertimeMultipliers = hourlyOvertimeMultipliers.length > 0 ? hourlyOvertimeMultipliers : dayRateOvertimeMultipliers;
  
  const level1Multiplier = escalationOvertimeMultipliers[0] || { code: 'OT', name: 'Overtime', defaultMultiplier: 1.5, perShiftBillingStructures: ['hourly', 'day_rate'], perWeekBillingStructures: ['hourly'] };
  const level2Multiplier = escalationOvertimeMultipliers[1] || escalationOvertimeMultipliers[0] || { code: 'DT', name: 'Double Time', defaultMultiplier: 2.0, perShiftBillingStructures: ['hourly', 'day_rate'], perWeekBillingStructures: ['hourly'] };

  const straightTimeExample = `${escalationStraightTimeMultiplier.defaultMultiplier}x ${escalationStraightTimeMultiplier.name}`;
  const level1Example = `${level1Multiplier.defaultMultiplier}x ${level1Multiplier.name}`;
  const level2Example = `${level2Multiplier.defaultMultiplier}x ${level2Multiplier.name}`;

  const rawCadence = Number(safeConfig.creditIntervalMinutes) || 15;
  const effectiveCadence = Math.max(rawCadence, 1);

  // Calculate multiplier offset example data
  const multiplierOffsetExamples = calculateMultiplierOffsetExamples({
    safeConfig,
    sampleRole,
    rateMultipliers,
    minimumBillableHours,
  });

  const multiplier_list_string = getMultiplierListString(rateMultipliers, sampleRole.billingStructure, true, 0);
  const multiplier_list_string_horizontal = getMultiplierListStringHorizontal(rateMultipliers, sampleRole.billingStructure);
  
  // Get the primary ladder to determine the example shift duration
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, sampleRole.billingStructure);
  const isDailyLadder = ladder.length > 0 && ladder[0].defaultPerShiftThresholdHours != null;
  
  let example_shift_duration;
  if (sampleRole.billingStructure === 'day_rate') {
    example_shift_duration = (minimumBillableHours || 10) + 4;
  } else if (isDailyLadder) {
    // For hourly roles with daily thresholds, show a longer shift to demonstrate the ladder
    const highestThreshold = Math.max(0, ...ladder.map(m => m.defaultPerShiftThresholdHours ?? 0));
    example_shift_duration = highestThreshold + 2;
  } else {
    // For hourly roles with weekly thresholds, use a realistic daily shift
    example_shift_duration = 12;
  }
  
  const normal_calculation_rows = generatePayCalculationRows(example_shift_duration, { roles, rateMultipliers, minimumBillableHours });

  const basisTier = rateMultipliers.find(m => m.code === safeConfig.premiumBasis);

  return {
    mealDeadlineHours: safeConfig.mealDeadlineHours || 5,
    mealBreakLengthMinutes: safeConfig.mealBreakLengthMinutes || 30,
    creditIntervalMinutes: effectiveCadence,
    timeCreditMinutes: safeConfig.timeCreditMinutes || 10,
    recurringMealDeadline: safeConfig.recurringMealDeadline,
    // Inverted flag for templates that need to show content when recurring is OFF.
    // markdownFormatter supports `{{#if x}}` but not `{{#unless x}}`, so we
    // materialize the negation here instead of relying on an unsupported helper.
    isNonRecurringMealDeadline: !safeConfig.recurringMealDeadline,
    isCountMode: safeConfig.mealTriggerMode === 'count',
    isDeadlineMode: safeConfig.mealTriggerMode !== 'count',
    fixedFeeSchedule: safeConfig.fixedFeeSchedule,
    feeScheduleString,
    fixedFeeAmount: safeConfig.fixedFeeAmount,
    example_shift_duration,
    gracePeriodMinutes: safeConfig.gracePeriodMinutes || 0,
    getSampleRole: sampleRole,
    sampleRoleUnit: sampleRole.billingStructure === 'day_rate' ? 'day' : 'hr',
    sampleRoleHourlyRate: sampleRole.billingStructure === 'day_rate'
      ? Math.round((sampleRole.rate / (minimumBillableHours || 10)) * 100) / 100
      : sampleRole.rate,
    minimumBillableHours,
    Math,
    formatTimeFromMinutes,
    formatCurrency,
    formatHours,
    formatTimeRange,
    meal_premium_type: safeConfig.premiumType,
    meal_premium_value: safeConfig.premiumValue != null ? Number(safeConfig.premiumValue) : (safeConfig.premiumType === 'percentage' ? 10 : 15),
    rateMultipliers,
    multiplier_list_string,
    isPremiumTypeFixed: safeConfig.premiumType === 'fixed',
    isPremiumTypePercentage: safeConfig.premiumType !== 'fixed',
    isOtInclusive: safeConfig.premiumBasis === 'ot_inclusive',
    multiplier_list_string_horizontal,
    single_offset_multiplier_list_str,
    double_offset_multiplier_list_str,
    straightTimeExample,
    level1Example,
    level2Example,
    useAdditionalOffset: safeConfig.useAdditionalOffset,
    stepUpAfterMinutes: safeConfig.stepUpAfterMinutes || 60,
    prevailingRateMultiplier: getPrevailingRateMultiplier(Number(safeConfig.mealDeadlineHours) || 5, rateMultipliers, sampleRole.billingStructure),
    meal_premium_basis: safeConfig.premiumBasis ?? 'STRAIGHT_TIME',
    premiumValue: safeConfig.premiumValue != null ? Number(safeConfig.premiumValue) : (safeConfig.premiumType === 'percentage' ? 10 : 15),
    premiumStacking_label: safeConfig.premiumStacking === 'stacked' ? 'per-missed-break' : 'flat',
    mealBasis_label: safeConfig.premiumBasis === 'ot_inclusive'
      ? 'earned rate'
      : basisTier ? `${basisTier.name} (${basisTier.defaultMultiplier}x)` : 'base rate',
    normal_calculation_rows,

    // from multiplierOffsetExamples
    totalPremium: formatCurrency(multiplierOffsetExamples.totalPremium),
    violationsSimulatedCount: multiplierOffsetExamples.violationsSimulatedCount,
    simulatedResetTime: multiplierOffsetExamples.simulatedResetTime,
    violation_total_minutes: multiplierOffsetExamples.violationTotalMinutes,
    violation_total_human: formatMinutesHuman(multiplierOffsetExamples.violationTotalMinutes),
    compoundOffsetSummary: multiplierOffsetExamples.compoundOffsetSummary,
    totalPremiumReset: formatCurrency(multiplierOffsetExamples.totalPremiumReset),
    multiplierOffsetTableHtml: multiplierOffsetExamples.multiplierOffsetTableHtml,
    multiplierOffsetTableResetHtml: multiplierOffsetExamples.multiplierOffsetTableResetHtml,
    
    breakBillingPolicy: safeConfig.breakBillingPolicy,
    mealBreakTypeLabel: (isBreakUnpaid(safeConfig.breakBillingPolicy, Number(safeConfig.mealBreakLengthMinutes) || 30) ? 'Unpaid' : 'Paid'),
    
    // Reset/break variables for multiplierOffsetExample's tables.
    // hourly_premium no longer uses these after migration to React injectables.
    resetBreakStartTime: multiplierOffsetExamples.resetBreakStartTime,
    resetBreakEndTime: multiplierOffsetExamples.resetBreakEndTime,
    resetViolation2StartTime: multiplierOffsetExamples.resetViolation2StartTime,
    resetShiftEndTime: multiplierOffsetExamples.resetShiftEndTime,
  };
}
